import { Tooltip } from "primereact/tooltip";
import styled from "styled-components";
import { IPriorAuthFinding } from "types/PriorAuth/PriorAuth";

const RelatedPanel = styled.div`
  margin-top: 0.5rem;
  padding: 0.6rem 0.75rem;
  border: 1px solid var(--gray-200);
  border-radius: 6px;
  background: var(--gray-50);
`;

const RelatedHeading = styled.p`
  margin: 0 0 0.15rem;
  font-weight: 600;
  font-size: 0.85rem;
  color: var(--gray-800);
`;

const RelatedNote = styled.p`
  margin: 0 0 0.5rem;
  font-size: 0.78rem;
  color: var(--gray-600);
`;

const RelatedRow = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0.35rem 0;

  & + & {
    border-top: 1px solid var(--gray-200);
  }
`;

const RelatedCode = styled.span`
  font-size: 0.83rem;
  color: var(--gray-800);

  code {
    font-weight: 700;
  }
`;

const RelatedRate = styled.span`
  font-size: 0.75rem;
  color: var(--gray-600);
`;

const Badge = styled.span<{ $unconfirmed: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  margin-top: 0.35rem;
  padding: 0.2rem 0.6rem;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 600;
  white-space: nowrap;
  cursor: default;

  /* Amber, not red. This is "do something before the service", not "this estimate is wrong" —
     red sits next to genuine pricing errors elsewhere in this table and would outrank them. */
  color: ${({ $unconfirmed }) =>
    $unconfirmed ? "var(--gray-700)" : "#8a5300"};
  background: ${({ $unconfirmed }) =>
    $unconfirmed ? "var(--gray-100)" : "#fdf1dc"};
  border: 1px solid
    ${({ $unconfirmed }) => ($unconfirmed ? "var(--gray-300)" : "#f2c98a")};
`;

interface IPriorAuthBadgeProps {
  finding?: IPriorAuthFinding;
}

/**
 * CLE-2766 — "Requires Authorization" on a CPT row.
 *
 * Why this exists: a payer can be billed for a service it never authorized and simply refuse to
 * pay, which shows up later as an E1 denial. The estimate is the last point where that is still
 * fixable, so the warning belongs here rather than in the claims screens where we already detect
 * it after the money is gone.
 *
 * TWO STATES, and the difference matters more than it looks:
 *
 *   REQUIRED — a list that definitely covers this payer names this code. Stated as fact.
 *
 *   PLAN_UNCONFIRMED — the list is scoped to particular plans and eligibility has not run, so we
 *   do not know whether this patient is on one of them. Ani (2026-09-02) asked for the plans to be
 *   named and for staff to be pointed at eligibility, rather than us either asserting a
 *   requirement that may not apply or saying nothing at all. Muted styling on purpose: a
 *   maybe must not look like a certainty, or the certainties stop being read.
 *
 * Renders nothing when there is no finding, which is the case for every code on every payer with
 * no list — so this component is invisible until an operator loads one.
 */
export function PriorAuthBadge({ finding }: IPriorAuthBadgeProps) {
  if (!finding) return null;

  const unconfirmed = finding.status === "PLAN_UNCONFIRMED";
  const tooltipId = `prior-auth-${finding.cptCode}`;

  const tooltip = unconfirmed
    ? `Prior authorization is required for ${finding.planKeywords.join(
        ", "
      )}. Run eligibility to check whether this patient is on one of those plans.`
    : `Prior authorization required${
        finding.listNames.length > 0 ? ` — ${finding.listNames.join(", ")}` : ""
      }`;

  const related = finding.relatedRequiringAuth ?? [];

  return (
    <>
      <Tooltip target={`.${tooltipId}`} position="top" />
      <Badge
        className={tooltipId}
        $unconfirmed={unconfirmed}
        data-testid={`priorAuthBadge.${finding.cptCode}`}
        data-pr-tooltip={tooltip}
      >
        <i className="pi pi-exclamation-circle" />
        {unconfirmed ? "Check Authorization" : "Requires Authorization"}
      </Badge>

      {related.length > 0 && (
        <RelatedPanel data-testid={`priorAuthRelated.${finding.cptCode}`}>
          <RelatedHeading>
            Commonly required with this authorization
          </RelatedHeading>
          <RelatedNote>
            Codes usually billed with this one that also need prior
            authorization.
          </RelatedNote>
          {related.map((item) => (
            <RelatedRow key={item.cptCode}>
              <RelatedCode>
                CPT <code>{item.cptCode}</code>
                {item.description ? ` — ${item.description}` : ""}
              </RelatedCode>
              <RelatedRate>
                Billed with this code in {Math.round(item.occurrenceRate * 100)}
                % of similar cases
              </RelatedRate>
            </RelatedRow>
          ))}
        </RelatedPanel>
      )}
    </>
  );
}

export default PriorAuthBadge;
